import { FormProvider } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import styled, { css } from 'styled-components';
import useFormWithErrorTracking from 'hooks/useFormWithErrorTracking';
import { useAtom, useAtomValue } from 'jotai';
import {
  bookingDurationAtom,
  bookingCaptainIdAtom,
  isBookingMultiDayAtom,
  bookingMessageAtom,
  bookingPassengersAtom,
  selectedBookingPackageAtom,
  tripFinishDayjsAtom,
  tripFinishFormattedAtom,
  tripStartDayjsAtom,
  tripStartFormattedAtom,
  bookingTripTimeAtom,
} from 'components/forms/bookingWidget/jotaiStore';
import {
  useBoatCalendar,
  useInitializeBookingFormData,
  useRudderStackOrderTraits,
} from 'components/forms/bookingWidget/hooks';
import OpenApiConfiguration from 'api/OpenApiConfiguration';
import { BoatPackage, DomesticV2BookingsInquiryPostRequest } from 'swagger/models';
import { useMutation } from 'react-query';
import { useTripCalculator } from 'hooks/queries';
import { BookingsApi } from 'swagger/apis/bookings-api';
import ChangeCaptainModal from 'components/pdp/v3/modals/ChangeCaptainModal';
import useModal from 'components/remodal/v2/useModal';
import { useUserDetails } from 'auth/mutations/userHooks';
import useLazySnackbar from 'components/banners/useLazySnackbar';
import useIncompleteUserProcessing from 'components/auth/hooks/useIncompleteUserProcessing';
import useRedirectToExistingBooking from 'components/checkout/v2/hooks/useRedirectToExistingBooking';
import { removeLocalBookingData } from 'components/checkout/v2/utils';
import Cookies from 'js-cookie';
import useAnalyticsTrack from 'hooks/useAnalyticsTrack';
import { useLocalStorage } from 'beautiful-react-hooks';
import boatsetterPaths from 'utils/boatsetterPaths';
import DateInput from 'components/forms/bookingWidget/DateInput';
import { constructBookingQueryString, valueEmpty, sha256HashString } from 'helpers';
import { InputWrap } from 'components/forms/bookingWidget/shared';
import DurationInput from 'components/forms/bookingWidget/DurationInput';
import StartTimeInput from 'components/forms/bookingWidget/StartTimeInput';
import CaptainPackageInput from 'components/forms/bookingWidget/CaptainPackageInput';
import PassengerInput from 'components/forms/bookingWidget/PassengerInput';
import TextInput from 'components/forms/bookingWidget/TextInput';
import ShortPriceSection from 'components/forms/bookingWidget/ShortPriceSection';
import LongPriceSection from 'components/forms/bookingWidget/LongPriceSection';
import SubmitMessageOwner from 'components/forms/bookingWidget/SubmitMessageOwner';
import SubmitBookingWidget from 'components/forms/bookingWidget/SubmitBookingWidget';
import { FlexCol } from '../../primitives/flex';
import { apiParamsHourNameMap } from './utils';
import { useBoatDetails } from '../../pdp/v3/hooks';
import { useBreakpoint } from '../../BreakpointProvider';
import useRudderStackTrack from '../../../hooks/useRudderStackTrack';
import { useTrackComponentEvent } from '../TimePicker/hooks/useTrackComponentEvent';

interface InquiryPostRequest extends DomesticV2BookingsInquiryPostRequest {
  captain_id?: string;
}

const bookingsApi = new BookingsApi(OpenApiConfiguration);

/**
 * This component is used in both the booking widget and the message owner modal. The isMessageOwner prop is used to
 * determine which component to render. This is tied into much render logic. Consider pulling this into two separate
 * components.
 */
interface BookingFormProps {
  boatId: string;
  // isExpanded and setExpanded are the two returned values from a useState<boolean> hook.
  isExpanded: ReturnType<typeof useState<boolean>>[0];
  setExpanded?: ReturnType<typeof useState<boolean>>[1];
  primaryPackage: BoatPackage;
  onSuccess?: (data) => void;
  isModal?: boolean;
  isMessageOwner?: boolean;
}
const BookingForm = ({
  boatId,
  isMessageOwner = false,
  isExpanded,
  primaryPackage,
  setExpanded = null,
  onSuccess = null,
  isModal = false,
}: BookingFormProps) => {
  const { enqueueSnackbar } = useLazySnackbar();
  const router = useRouter();
  const rudderTrack = useRudderStackTrack();
  const eventIntakeTrack = useAnalyticsTrack();
  const trackComponentEvent = useTrackComponentEvent();

  const [tripStart] = useAtom(tripStartDayjsAtom);
  const tripFinish = useAtomValue(tripFinishDayjsAtom);
  const tripStartFormatted = useAtomValue(tripStartFormattedAtom);
  const tripFinishFormatted = useAtomValue(tripFinishFormattedAtom);
  const [tripTime] = useAtom(bookingTripTimeAtom);
  const isBookingMultiDay = useAtomValue(isBookingMultiDayAtom);
  const passengersCount = useAtomValue(bookingPassengersAtom);
  const [bookingMessage] = useAtom(bookingMessageAtom);
  const [bookingDuration] = useAtom(bookingDurationAtom);

  const [selectedBookingPackage] = useAtom(selectedBookingPackageAtom);

  const bookingCaptainId = useAtomValue(bookingCaptainIdAtom);

  const rudderStackOrderTraits = useRudderStackOrderTraits();
  const { tripCalculation } = useTripCalculator();

  const { userId, isAuthenticated } = useUserDetails();
  const { boatDetails } = useBoatDetails(boatId);
  const { handlePossibleIncompleteAccountInApp } = useIncompleteUserProcessing();
  const [galacticaSession] = useLocalStorage('galactica_session', '');

  const { boatCalendar } = useBoatCalendar({
    startOn: tripStart?.startOf('month')?.format('YYYY-MM-DD'),
    endOn: tripFinish?.add(1, 'month')?.format('YYYY-MM-DD'),
    boatId,
    packageType: selectedBookingPackage?.package_type,
  });

  const dayIsAvailable = !valueEmpty(boatCalendar) && !!boatCalendar[tripStart?.format('YYYY-MM-DD')]?.available;

  // This feature is BROKEN. A race condition with the form submitting and continuing to the next page prevents the
  // ability to use it.
  const {
    modal: changeCaptainModal,
    openModal: openChangeCaptainModal,
    closeModal,
  } = useModal(<ChangeCaptainModal boatId={boatId} />, {
    isOnRight: true,
    notScrollAble: true,
    trackingName: 'change_captain_modal',
  });

  const { mutate: createBookingInquiryMutate, isLoading: processing } = useMutation(
    ({ body }: { body: InquiryPostRequest }) => bookingsApi.domesticV2BookingsInquiryPost(body),
    {
      onSuccess({ data }: { data: any }) {
        rudderTrack('booking_inquiry_created', {
          booking_id: data?.id,
        });
        rudderTrack('customSiteEvent', {
          category: 'bookingMessageOwnerSent',
          action: 'booking message owner',
          label: 'SendMessage',
          // Defensive due to the `any` type
          renterEmail: data?.renter?.email && sha256HashString(data.renter.email),
        });
        removeLocalBookingData(false, {});

        rudderTrack('message_owner_success', {
          ...rudderStackOrderTraits,
          booking_id: data?.id,
        });
        onSuccess?.(data);
      },
      onError({
        response: {
          data: {
            error_message,
            error_code,
            data: { message },
          },
        },
      }) {
        if (error_code === 'validation_error' && Array.isArray(message) && message[0] === 'contained spam') {
          setError('message', {
            message:
              'To protect users, messages that contain certain words, phone numbers, email addresses or website links are not allowed.',
          });
          return;
        }
        enqueueSnackbar(error_message, { variant: 'error' });
      },
    }
  );

  const methods = useFormWithErrorTracking({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      trip_start: null,
      duration: null,
      trip_time: null,
      message: null,
      passengers: null,
    },
  });
  const { handleSubmit, register, errors, setValue, watch, setError } = methods;

  useInitializeBookingFormData(boatId, primaryPackage);
  const existingBookingId = useRedirectToExistingBooking(boatId);

  // sync store values with form validation
  useEffect(() => {
    setValue('trip_start', tripStartFormatted, { shouldValidate: !!tripStart });
    setValue('trip_time', tripTime?.value, { shouldValidate: !!tripTime });
    setValue('duration', bookingDuration?.value, { shouldValidate: !!bookingDuration });
    setValue('message', bookingMessage, { shouldValidate: !!bookingMessage });
    setValue('passengers', passengersCount, { shouldValidate: !!passengersCount });
  }, [tripStart, tripTime, bookingDuration, bookingMessage, passengersCount, setValue, tripStartFormatted]);

  const onSubmit = async (values) => {
    const addCaptainId = bookingCaptainId && boatDetails.captain_network_enabled;

    const checkoutParameters = {
      boat_public_id: boatId,
      tz: boatDetails.time_zone,
      package_public_id: selectedBookingPackage.id,
      package_type: selectedBookingPackage.package_type,
      passengers: passengersCount,
      is_multi_day: isBookingMultiDay,
      ...values,
      duration: apiParamsHourNameMap[values.duration],
      trip_start: values.trip_start,
      trip_finish: tripFinishFormatted || tripStartFormatted,
      ...(addCaptainId && { captain_public_id: bookingCaptainId }),
    };

    if (isMessageOwner) {
      createBookingInquiryMutate({
        body: {
          message: values.message,
          trip_time: values.trip_time,
          duration: apiParamsHourNameMap[values.duration],
          package_id: selectedBookingPackage.id,
          trip_start: values.trip_start,
          trip_finish: tripFinishFormatted || tripStartFormatted,
          passengers: values.passengers,
          ...(addCaptainId && { captain_id: bookingCaptainId }),
        },
      });

      return;
    }

    // This feature is BROKEN. A race condition with the form submitting and continuing to the next page prevents the
    // ability to use it.
    if (!isMessageOwner && 'is_captain_available' in tripCalculation && !tripCalculation.is_captain_available) {
      enqueueSnackbar(
        'The captain assigned is not available during the time selected. Please assign another captain.',
        {
          variant: 'error',
          autoHideDuration: 4000,
          onClick: openChangeCaptainModal,
        }
      );
    }

    const urlParameters = constructBookingQueryString(checkoutParameters);
    const bookNowUrl = `/a/bookings/book_now?${urlParameters}`;
    const signupUrl = `${boatsetterPaths.signUp}?redirect_to=${encodeURIComponent(bookNowUrl)}`;

    if (isAuthenticated) {
      rudderTrack('request_to_book_success', {
        type: 'expanded_floating',
        ...rudderStackOrderTraits,
      });
    }

    // if not authenticated, go to signup
    if (!isAuthenticated) {
      router.push(signupUrl);
      return;
    }

    const completeAccount = await handlePossibleIncompleteAccountInApp(bookNowUrl);

    // if an account is completed, go to book now
    if (completeAccount) {
      if (existingBookingId) {
        router.push(`/a/bookings/${existingBookingId}/book_now`);
        return;
      }

      closeModal();
      router.push(bookNowUrl);
    }
  };

  const breakpoints = useBreakpoint();
  const smallScreen = breakpoints.xs || breakpoints.sm;

  const trackRequestToBookStart = () => {
    trackComponentEvent({
      event_name: 'click',
      component_type: 'button',
      object: 'request_to_book',
      context: 'booking_widget',
      object_properties: rudderStackOrderTraits,
    });
    rudderTrack('request_to_book_start', {
      type: isExpanded ? 'expanded_floating' : 'initial_floating',
      ...rudderStackOrderTraits,
    });

    if (selectedBookingPackage.instant_bookable) {
      eventIntakeTrack(
        'UserDidInstantBooking',
        {
          anonymousId: Cookies.get('uuid') || '',
          sessionId: galacticaSession || '',
          appScreen: router.pathname,
        },
        {
          boatId,
        }
      );
    } else {
      eventIntakeTrack(
        'UserRequestedBooking',
        {
          anonymousId: Cookies.get('uuid') || '',
          sessionId: galacticaSession || '',
          appScreen: router.pathname,
        },
        {
          boatId,
        }
      );
    }
  };

  const messageOwnerWrapperRef = useRef();

  const viewerIsOwner = userId === boatDetails?.primary_manager?.id;

  return (
    <FormProvider {...methods}>
      <Form as="form" onSubmit={handleSubmit(onSubmit)} isModal={isModal}>
        <DateInput
          isModal={isModal}
          isMessageOwner={isMessageOwner}
          smallScreen={smallScreen}
          errors={errors}
          register={register}
          boatDetails={boatDetails}
          primaryPackage={primaryPackage}
          boatCalendar={boatCalendar}
        />

        <DurationInput isModal={isModal} errors={errors} register={register} boatCalendar={boatCalendar} />

        <StartTimeInput
          isModal={isModal}
          errors={errors}
          register={register}
          boatCalendar={boatCalendar}
          setExpanded={setExpanded}
        />

        {isExpanded && <CaptainPackageInput boatDetails={boatDetails} />}

        {isExpanded && <PassengerInput isModal={isModal} register={register} />}

        {isExpanded && isMessageOwner && (
          <TextInput
            messageOwnerWrapperRef={messageOwnerWrapperRef}
            errors={errors}
            register={register}
            watch={watch}
          />
        )}

        {isExpanded && isMessageOwner && !smallScreen && <ShortPriceSection />}

        {isExpanded && !isMessageOwner && <LongPriceSection />}

        {isMessageOwner && (
          <SubmitMessageOwner isSmallScreen={smallScreen} isProcessing={processing} isViewerOwner={viewerIsOwner} />
        )}
        {!isMessageOwner && (
          <SubmitBookingWidget
            isProcessing={processing}
            isDayAvailable={dayIsAvailable}
            trackRequestToBookStart={trackRequestToBookStart}
            boatId={boatId}
          />
        )}
      </Form>
      {changeCaptainModal}
    </FormProvider>
  );
};

const PricingSection = styled(FlexCol)`
  padding-top: 30px;
`;

const Form = styled(FlexCol)<{ isModal: boolean }>`
  ${({ isModal }) =>
    isModal &&
    css`
      gap: 15px;

      ${InputWrap} {
        padding: 0;
      }

      ${PricingSection} {
        padding-top: 20px;
      }
    `}
`;

export const DurationIcon = () => (
  <svg width="17" height="18" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="7.5" cy="10.5" r="6.9" stroke="#5E696A" strokeWidth="1.2" />
    <line x1="7.5" y1="4" x2="7.5" y2="2" stroke="#5E696A" />
    <line x1="7.5" y1="10.5" x2="7.5" y2="6.5" stroke="#5E696A" strokeLinecap="round" />
    <line x1="13.8828" y1="6.79892" x2="14.7488" y2="6.29892" stroke="#5E696A" />
    <line x1="6.6" y1="1.4" x2="8.4" y2="1.4" stroke="#5E696A" strokeWidth="1.2" strokeLinecap="round" />
    <line
      x1="14.8196"
      y1="5.21962"
      x2="15.7196"
      y2="6.77846"
      stroke="#5E696A"
      strokeWidth="1.2"
      strokeLinecap="round"
    />
    <circle cx="7.5" cy="11" r="1" fill="#5E696A" />
  </svg>
);

export default BookingForm;
